import { Injectable } from '@angular/core';
import { BasketItemSource } from '@epicuro-next/models/basket';
import {
  selectIsSetSourceNeed,
  selectSetDefaultListSourceNeeded,
  selectSetDefaultSearchSourceNeeded,
} from '@epicuro-next/state/config-strip-state';
import { selectListEntityFactory } from '@epicuro-next/state/list-entities';
import { selectHorizontalViewEnabledSettingValue } from '@epicuro-next/state/p-basket-state';
import { shopTheList } from '@epicuro-next/state/product-list-state';
import { selectRouteParams } from '@epicuro-next/state/router-store';
import { recommendationItemClicked } from '@epicuro-next/state/tracking';
import { ensureArray, isNonNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import { filter, map, tap } from 'rxjs/operators';

import { setProductSource } from '../actions';
import { activateConfigStrip } from '../actions/activate.actions';
import { ConfigStripActivationSource } from '../models';

@Injectable()
export class ConfigStripSourceEffects {
  public setFallBackSourceListOnActivateCS$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activateConfigStrip),
      filter(
        ({ source }) => source.source === ConfigStripActivationSource.List,
      ),
      concatLatestFrom(() =>
        this.store.pipe(select(selectSetDefaultListSourceNeeded)),
      ),
      filter(
        ([action, isSetSourceNeed]) =>
          action.setSourceNeeded || isSetSourceNeed,
      ),
      map(([{ source }]) =>
        setProductSource({
          source: source.subSource ?? BasketItemSource.LIST_LOCAL,
          details: source.details,
        }),
      ),
    ),
  );

  public setSourceSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activateConfigStrip),
      filter(
        ({ source }) => source.source === ConfigStripActivationSource.Search,
      ),
      concatLatestFrom(() => [
        this.store.pipe(select(selectSetDefaultSearchSourceNeeded)),
        this.store.pipe(select(selectIsSetSourceNeed)),
        this.store.pipe(select(selectHorizontalViewEnabledSettingValue)),
      ]),
      filter(
        ([action, isSetSourceNeed, isSetSourceNeededByState]) =>
          action.setSourceNeeded || isSetSourceNeed || isSetSourceNeededByState,
      ),
      map(([{ source }, _, __, isTable]) =>
        setProductSource({
          source: source.subSource ?? BasketItemSource.SEARCH,
          details: {
            ...source.details,
            ...(isTable ? { isTable: true } : { isTable: undefined }),
          },
        }),
      ),
    ),
  );

  public setSourceOnRecommendationClicked = createEffect(() =>
    this.actions$.pipe(
      ofType(recommendationItemClicked),
      map(
        ({
          panelName,
          panelStyle,
          servingConfig,
          correlationId,
          attributionToken,
          providerId,
          hash,
        }) =>
          setProductSource({
            source: BasketItemSource.RECOMMENDATION_PANEL,
            details: {
              recommendationPanel: {
                panelName,
                panelStyle,
                servingConfig,
                correlationId,
                attributionToken,
                providerId,
                hash,
              },
            },
          }),
      ),
    ),
  );

  public setFallBackSourceListOnShopTheList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(shopTheList),
      concatLatestFrom(() => [
        this.store.pipe(select(selectRouteParams)),
        this.store.pipe(select(selectSetDefaultListSourceNeeded)),
      ]),
      filter(([, _, isSetSourceNeed]) => isSetSourceNeed),
      map(([_, params]) => {
        return {
          lookBookId: params?.lookBookId,
          listId: params?.listId ?? params?.id,
        };
      }),
      filter(({ listId }) => !!listId),
      map(({ listId, lookBookId }) => {
        const details = lookBookId
          ? { listId: [listId], lookBookId: [lookBookId] }
          : { listId: [listId] };
        return setProductSource({
          source: BasketItemSource.LIST_LOCAL,
          details,
        });
      }),
    ),
  );

  public captureListSourceTypeError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(setProductSource),
        filter(({ details }) => ensureArray(details?.listId).length !== 0),
        concatLatestFrom(({ details }) => {
          const listId = ensureArray(details?.listId)[0];
          return this.store.pipe(select(selectListEntityFactory(listId)));
        }),
        filter(([{ details }, listEntity]) => {
          return (
            isNonNullish(listEntity) &&
            isNonNullish(details?.listType) &&
            details?.listType?.toLowerCase() !==
              listEntity?.list?.type?.toLowerCase()
          );
        }),
        tap(([{ details }, listEntity]) => {
          const message = `No correct source list type ${details?.listType} information for list ${listEntity?.list?.id}`;
          console.error(message);
          Sentry.captureException(new Error(message));
        }),
      ),
    { dispatch: false },
  );

  constructor(private actions$: Actions, private store: Store) {}
}
